import { useState, useMemo, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MenuItem } from '@mui/material';
import {
  Autocomplete,
  Flex,
  FormField,
  TextField,
  formFieldRequiredLabel as requiredLabel,
} from '@elementary/common-ui/components';
import { JIRA_FIELD_AUTOCOMPLETE } from '@elementary/common-ui/constants/queryKeys';
import useDebounce from '@elementary/common-ui/hooks/useDebounce';
import { Controller, useFormContext } from 'react-hook-form';
import { fetchJiraFieldAutocomplete } from '../api';
import type { ProjectField } from '../types';

type UserOption = {
  value: string;
  label: string;
  icon?: ReactNode;
};

type Props = {
  field: ProjectField;
  label: string;
  envId: string;
  integrationId: string;
  projectId: string;
};

const JiraUserAutocompleteField = ({
  field,
  label,
  envId,
  integrationId,
  projectId,
}: Props) => {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 200);

  const formMethods = useFormContext<Record<string, any>>();

  const hasAutocomplete =
    !!field.auto_complete_url && !field.disable_auto_complete;

  const { data, isLoading } = useQuery({
    queryKey: [
      JIRA_FIELD_AUTOCOMPLETE,
      envId,
      integrationId,
      field.key,
      debouncedQuery,
      projectId,
    ],
    queryFn: () =>
      fetchJiraFieldAutocomplete(
        envId,
        integrationId,
        field.auto_complete_url!,
        debouncedQuery,
        projectId,
      ),
    enabled: hasAutocomplete && !!envId,
    select: (data) => data.suggestions,
  });

  const suggestions: UserOption[] = useMemo(() => {
    const rawOptions =
      (data && data.length > 0 ? data : field.allowed_values) || [];
    return rawOptions.map((option: any) => ({
      ...option,
      icon: option.icon ? (
        <img
          src={option.icon}
          alt={option.label || ''}
          style={{ width: 16, height: 16, borderRadius: '50%' }}
        />
      ) : undefined,
    }));
  }, [data, field.allowed_values]);

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: UserOption,
  ) => (
    <MenuItem {...props}>
      <Flex alignItems="center" gap={1}>
        {option.icon}
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {option.label}
        </span>
      </Flex>
    </MenuItem>
  );

  const handleInputChange = (_: any, value: string) => {
    setQuery(value);
  };

  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  if (
    !hasAutocomplete &&
    (!field.allowed_values || field.allowed_values.length === 0) &&
    !isLoading
  ) {
    return (
      <FormField
        title={label}
        optional={!field.required}
        errorMessage={
          formMethods.formState.errors[field.key]?.message as string
        }
      >
        <TextField
          value="Not available"
          size="large"
          disabled
          placeholder="Re-authenticate Jira to enable"
        />
      </FormField>
    );
  }

  return (
    <FormField
      title={label}
      optional={!field.required}
      errorMessage={formMethods.formState.errors[field.key]?.message as string}
    >
      <span onClick={stopPropagation}>
        <Controller
          name={field.key}
          control={formMethods.control}
          rules={{ required: field.required ? requiredLabel : false }}
          render={({ field: controllerField }) => {
            const isMultiple = field.schema_info?.type === 'array';
            const value = controllerField.value || (isMultiple ? [] : null);
            return (
              <Autocomplete
                size="large"
                placeholder={`Select ${label.toLowerCase()}`}
                options={suggestions}
                loading={isLoading}
                value={value}
                onChange={(_, newValue) => {
                  controllerField.onChange(newValue);
                }}
                onInputChange={handleInputChange}
                multiple={isMultiple}
                renderOption={renderOption}
                listboxMaxHeight={240}
                listboxTitle={'Suggestions'}
              />
            );
          }}
        />
      </span>
    </FormField>
  );
};

export default JiraUserAutocompleteField;
